/**
 * AdminSoftDeleteModuleModal — potwierdzenie wycofania modułu z katalogu (T8.1b).
 *
 * Backend: DELETE /api/admin/modules/:code, body `{reason}` (wymagany, min 1 char).
 * Walidacja po stronie backend: 403 CANNOT_RETIRE_CORE_MODULE jesli is_core=true,
 * 422 VALIDATION_FAILED jesli reason pusty/za dlugi.
 *
 * Po sukcesie: onSuccess callback (AdminModules wykona invalidate query + toast).
 *
 * Plan: docs/phases/PHASE_8_PLAN.md §T8.1b + ADR-0011.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface AdminSoftDeleteModuleModalProps {
  open: boolean;
  moduleCode: string;
  moduleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminSoftDeleteModuleModal({
  open,
  moduleCode,
  moduleName,
  onClose,
  onSuccess,
}: AdminSoftDeleteModuleModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const trimmed = reason.trim();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (trimmed.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.delete(`/admin/modules/${moduleCode}`, { data: { reason: trimmed } });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const status = axErr.response?.status ?? null;
      const code = axErr.response?.data?.error ?? null;
      if (status === 403 && code === 'CANNOT_RETIRE_CORE_MODULE') {
        setError(t('admin.modules.softDelete.errors.coreBlocked'));
      } else if (status === 422) {
        setError(t('admin.modules.softDelete.errors.validation'));
      } else {
        setError(t('admin.modules.softDelete.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.modules.softDelete.title', { name: moduleName })}
      description={t('admin.modules.softDelete.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.modules.softDelete.cancel')}
          </Button>
          <Button
            type="submit"
            form="soft-delete-module-form"
            variant="danger"
            loading={submitting}
            disabled={submitting || trimmed.length === 0}
          >
            {submitting
              ? t('admin.modules.softDelete.submitting')
              : t('admin.modules.softDelete.confirm')}
          </Button>
        </>
      }
    >
      <form
        id="soft-delete-module-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <p className="text-sm text-text-tertiary">{t('admin.modules.softDelete.body')}</p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.modules.softDelete.reasonLabel')}
          </span>
          <textarea
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-border-primary dark:bg-bg-tertiary"
            rows={3}
            maxLength={500}
            required
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t('admin.modules.softDelete.reasonPlaceholder')}
          />
        </label>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
